import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { promptApi } from '../services/api';
import { BrandSafetyPrompt } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { CAPTIONED_TRIGGER_RESTING_CLASSES, CaptionLabel } from './CaptionedField';

interface AnalysisProfileSelectProps {
  value: string;
  onChange: (promptId: string) => void;
  placeholder?: string;
  className?: string;
  /** Optional extra classes merged onto the trigger control itself (e.g. to bump it onto the 42px row-control standard for a specific toolbar) — leaves the default trigger sizing untouched for callers that don't pass it. */
  triggerClassName?: string;
  required?: boolean;
  disabled?: boolean;
  /** Optional: Pass prompts from parent to avoid duplicate API calls */
  externalPrompts?: BrandSafetyPrompt[];
  /** Optional: Called when the current `value` fails to resolve via getById (e.g. deleted/inaccessible blueprint) */
  onInvalidSelection?: () => void;
  /**
   * Trigger height: `sm` (default) keeps this component's own `py-2`
   * sizing — the RelevanceEnginePage config row (deepcontext-b5ak) and
   * Insights/MediaPlanning callers rely on that exact height for parity
   * with their own py-2 controls. `md` opts into the app's 42px
   * row-control standard (`h-[42px]`, see _config/conventions.md) for
   * callers whose row is built from the shared 42px primitives instead
   * (AnalysisHistory's toolbar, deepcontext-hkt0.6).
   */
  size?: 'sm' | 'md';
  /**
   * Opt-in "Blueprint" caption rendered above the trigger, plus the matching
   * per-theme accent treatment on the resting (closed, non-hover) pill
   * border/background/value/chevron (deepcontext-tpe1). Lives here — not on
   * each caller — so the caption and its styling can't drift between pages
   * the way the old per-page label placement did (deepcontext-knin).
   */
  captioned?: boolean;
}

// Debounce delay for async search
const SEARCH_DEBOUNCE_MS = 300;

// Open-list width-to-content cap (deepcontext-sry4) — matches the
// `max-w-[min(520px,calc(100vw-48px))]` class below; used to decide whether
// the list would overflow the viewport on the right and should instead
// align to the pill's right edge.
const OPEN_LIST_MAX_WIDTH_PX = 520;
const VIEWPORT_MARGIN_PX = 48;

const AnalysisProfileSelect: React.FC<AnalysisProfileSelectProps> = ({
  value,
  onChange,
  placeholder = 'Select a brand blueprint',
  className = '',
  triggerClassName = '',
  required = false,
  disabled = false,
  externalPrompts,
  onInvalidSelection,
  size = 'sm',
  captioned = false
}) => {
  const heightClass = size === 'md' ? 'h-[42px]' : 'py-2';
  const { user } = useAuth();
  const { selectedCompanyId } = useCompany();
  const [prompts, setPrompts] = useState<BrandSafetyPrompt[]>([]);
  const [filteredPrompts, setFilteredPrompts] = useState<BrandSafetyPrompt[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  // If externalPrompts is provided, don't show loading state (parent handles loading)
  const [loading, setLoading] = useState(externalPrompts === undefined);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPromptCache, setSelectedPromptCache] = useState<BrandSafetyPrompt | null>(null);
  // Whether the open list should hug the pill's right edge instead of its
  // left edge — flips when the widened list would otherwise run off the
  // right of the viewport (deepcontext-sry4).
  const [alignRight, setAlignRight] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchAbortController = useRef<AbortController | null>(null);
  const lastSearchKey = useRef<string | null>(null); // Track last search to prevent duplicate calls (null = never searched)

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Use external prompts if provided (avoids duplicate API calls)
  useEffect(() => {
    if (externalPrompts !== undefined) {
      setPrompts(externalPrompts);
      setFilteredPrompts(externalPrompts);
      setLoading(false);
    }
  }, [externalPrompts]);

  // Client-side filtering when using external prompts
  useEffect(() => {
    if (externalPrompts !== undefined) {
      if (!searchTerm.trim()) {
        setFilteredPrompts(prompts);
      } else {
        const filtered = prompts.filter(prompt =>
          prompt.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          prompt.description?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredPrompts(filtered);
      }
    }
  }, [searchTerm, prompts, externalPrompts]);

  // Server-side search when debounced search term changes (only when NOT using external prompts)
  // IMPORTANT: Check for undefined specifically - if externalPrompts is provided (even as empty array),
  // the parent is responsible for loading data and we should NOT make our own API calls
  useEffect(() => {
    if (externalPrompts === undefined) {
      // Prevent duplicate API calls with same parameters
      const searchKey = `${debouncedSearchTerm}|${selectedCompanyId || ''}`;
      if (searchKey !== lastSearchKey.current) {
        lastSearchKey.current = searchKey;
        searchPrompts(debouncedSearchTerm);
      }
    }
  }, [debouncedSearchTerm, selectedCompanyId, externalPrompts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Decide left- vs right-alignment for the open list based on how much
  // room is left to the right of the pill (deepcontext-sry4). Left-aligned
  // is the default (matches the locked design); flip to right-aligned only
  // when the widened list would overflow the viewport.
  useLayoutEffect(() => {
    if (!isOpen || !dropdownRef.current) return;
    const rect = dropdownRef.current.getBoundingClientRect();
    const spaceToRight = window.innerWidth - rect.left;
    setAlignRight(spaceToRight < OPEN_LIST_MAX_WIDTH_PX + VIEWPORT_MARGIN_PX);
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch selected prompt details if value is set but not in current list
  useEffect(() => {
    if (!value || prompts.find(p => p.id === value) || selectedPromptCache) {
      return;
    }
    const requiresCompanySelection = user?.role === 'eskimi_admin' || user?.role === 'adops';
    // CompanyContext resolves selectedCompanyId asynchronously, so this can
    // fire once with it still null right after mount for eskimi_admin/adops
    // — skip the lookup (and critically, do NOT report it as invalid) until
    // the real company arrives and this effect re-fires. Without this guard
    // a persisted/deep-linked company-owned blueprint 404s against
    // canUserReadPrompt (no companyId yet) and onInvalidSelection wrongly
    // clears it before selectedCompanyId ever gets a chance to resolve
    // (bd deepcontext-q2bz.1, same race class as bd-jvwh.1).
    if (requiresCompanySelection && !selectedCompanyId) {
      return;
    }
    fetchSelectedPrompt(value);
  }, [value, prompts, selectedCompanyId, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSelectedPrompt = async (promptId: string) => {
    try {
      const requiresCompanySelection = user?.role === 'eskimi_admin' || user?.role === 'adops';
      const companyIdToUse = requiresCompanySelection ? selectedCompanyId || undefined : undefined;
      const prompt = await promptApi.getById(promptId, companyIdToUse);
      if (prompt) {
        setSelectedPromptCache(prompt);
      }
    } catch (err) {
      console.error('Failed to fetch selected prompt:', err);
      // The current value no longer resolves (e.g. blueprint deleted or
      // inaccessible). Notify the parent so it can clear the stale selection.
      onInvalidSelection?.();
    }
  };

  const searchPrompts = useCallback(async (search: string) => {
    // Cancel any pending request — this aborts the in-flight axios call,
    // not just the JS state-handling path, because we now forward the
    // signal to the underlying fetch (bd-sqi).
    if (searchAbortController.current) {
      searchAbortController.current.abort();
    }

    // Capture *this* request's controller in a local so the success/finally
    // paths can compare against the latest controller on the ref. Without
    // this, a stale request that resolves late would flip `searching` back
    // to false (or worse, clobber `prompts`) on behalf of a newer request.
    const controller = new AbortController();
    searchAbortController.current = controller;

    try {
      setSearching(true);

      // Pass selected company ID for eskimi_admin users
      const companyIdToUse = (user?.role === 'eskimi_admin' || user?.role === 'adops') ? selectedCompanyId || undefined : undefined;

      // Use paginated API with search for server-side filtering
      const response = await promptApi.getAllPaginated(
        {
          page: 1,
          pageSize: 50, // Limit dropdown results
          search: search || undefined,
          companyId: companyIdToUse
        },
        { signal: controller.signal }
      );

      // Guard: if this request was aborted (or superseded by a newer one)
      // between the await resolving and now, do NOT touch state.
      if (controller.signal.aborted || searchAbortController.current !== controller) {
        return;
      }

      setPrompts(response.prompts);
      setFilteredPrompts(response.prompts);
      setError(null);
      setLoading(false);
    } catch (err: any) {
      // Axios throws a CanceledError when its signal aborts; native
      // fetch throws a DOMException with name === 'AbortError'. Both are
      // expected outcomes of cancelling — not real errors.
      const errName = err?.name;
      if (errName === 'AbortError' || errName === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        return;
      }
      // Also skip if this request was aborted but somehow surfaced a
      // different error class — the user has moved on.
      if (controller.signal.aborted) {
        return;
      }
      console.error('Failed to search brand blueprints:', err);
      setError('Failed to load brand blueprints');
    } finally {
      // Only the latest controller is allowed to flip `searching` off.
      // An older, aborted request resolving late must NOT hide the spinner
      // while a newer request is still running.
      if (searchAbortController.current === controller) {
        setSearching(false);
      }
    }
  }, [user?.role, selectedCompanyId]);

  // Find selected prompt from current list or cache
  const selectedPrompt = prompts.find(p => p.id === value) || selectedPromptCache;

  const handleSelect = (promptId: string) => {
    // Cache the selected prompt for display
    const selected = prompts.find(p => p.id === promptId);
    if (selected) {
      setSelectedPromptCache(selected);
    }
    onChange(promptId);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  if (loading) {
    return (
      <div className={`relative ${className}`}>
        <div className={`w-full flex items-center ${heightClass} px-4 border border-line rounded-full bg-ghost-bg text-ink-muted text-sm`}>
          Loading brand blueprints...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`relative ${className}`}>
        <div className={`w-full flex items-center ${heightClass} px-4 border border-risk rounded-full bg-risk-bg text-risk text-sm`}>
          {error}
        </div>
      </div>
    );
  }

  // Resting-state (closed, non-hover) accent treatment, opt-in via
  // `captioned` (deepcontext-tpe1). Dark keeps a faint accent wash; light
  // gets a pure white surface with a heavier accent border instead — a
  // violet wash on light sinks the pill into the #F4EDFF canvas. Only
  // applies when the trigger isn't already showing the open-state
  // border-accent/ring treatment (visually distinct) or the disabled state
  // (Analysis Audit's blueprintField passes disabled={isLoading} alongside
  // captioned — disabled must still read as the plain muted bg-ghost-bg
  // look every other trigger uses, not a mix of the two backgrounds/borders).
  const captionedRestingClasses = captioned && !disabled
    ? CAPTIONED_TRIGGER_RESTING_CLASSES
    : '';
  const valueColorClass = captioned
    ? (selectedPrompt ? 'text-accent-active dark:text-ink' : 'text-ink-muted')
    : (selectedPrompt ? 'text-ink' : 'text-ink-muted');
  const chevronColorClass = captioned ? 'text-accent' : 'text-ink-muted';

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {captioned && <CaptionLabel label="Blueprint" />}
      {/* Selected value display / Search input */}
      <div
        data-testid="analysis-profile-trigger"
        className={`w-full border cursor-pointer flex items-center justify-between rounded-full ${heightClass} px-4 text-sm font-bold ${
          disabled ? 'bg-ghost-bg cursor-not-allowed opacity-75' : `${captioned ? '' : 'bg-surface'} hover:border-accent/40`
        } ${
          isOpen ? 'border-accent ring-1 ring-accent' : (captioned && !disabled ? captionedRestingClasses : 'border-line')
        } ${triggerClassName}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        {isOpen ? (
          <div className="flex items-center w-full">
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search brand blueprints..."
              className="w-full outline-none bg-surface text-ink"
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
            {/* Always-mounted spinner: opacity-toggled rather than
                conditionally rendered, so React never tears down adjacent
                DOM (which would force the input to lose focus on some
                browsers and contributes to the perceived "freeze"). */}
            <svg
              data-testid="search-spinner"
              aria-hidden={!searching}
              className={`animate-spin h-4 w-4 text-ink-muted ml-2 flex-shrink-0 transition-opacity ${searching ? 'opacity-100' : 'opacity-0'}`}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        ) : (
          <span
            className={`${valueColorClass} truncate block`}
            title={selectedPrompt ? selectedPrompt.name : undefined}
          >
            {selectedPrompt ? selectedPrompt.name : placeholder}
          </span>
        )}
        <svg
          className={`w-5 h-5 ${chevronColorClass} transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          className={`absolute z-50 min-w-full w-max max-w-[min(520px,calc(100vw-48px))] mt-1 bg-surface border border-line rounded-lg shadow-lg ${
            alignRight ? 'right-0' : 'left-0'
          }`}
          style={{ maxHeight: '15rem', overflowY: 'auto', overscrollBehavior: 'contain' }}
          onScroll={(e) => e.stopPropagation()}
          onWheel={(e) => e.stopPropagation()}
        >
          {filteredPrompts.length === 0 && !searching ? (
            <div className="px-3 py-2 text-sm text-ink-muted">
              {searchTerm ? 'No brand blueprints found' : 'No brand blueprints available'}
            </div>
          ) : (
            filteredPrompts.map((prompt) => (
              <div
                key={prompt.id}
                className={`px-3 py-2 cursor-pointer hover:bg-ghost-bg ${
                  prompt.id === value ? 'bg-nav-active text-accent-active' : 'text-ink'
                }`}
                onClick={() => handleSelect(prompt.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0 mr-2">
                    <div
                      className={`text-sm font-medium break-words ${
                        prompt.id === value ? '' : 'text-ink'
                      }`}
                    >
                      {prompt.name}
                    </div>
                    {prompt.description && (
                      <div className="text-xs text-ink-muted mt-0.5 truncate">{prompt.description}</div>
                    )}
                  </div>
                  {prompt.companyId && (
                    <span className="text-xs text-ink-muted flex-shrink-0">Company</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default AnalysisProfileSelect;
